import React, { useEffect, useState } from 'react';
import { Theme } from '../../types/styled';
import { ThemeContext, ThemeMode } from '../../theme/ThemeProvider';

/**
 * Default theme that will be used if the main theme is missing
 */
const fallbackTheme: Theme = {
  colors: {
    primary: {
      '50': '#e3f2fd',
      '100': '#bbdefb',
      '200': '#90caf9',
      '300': '#64b5f6',
      '400': '#42a5f5',
      '500': '#2196f3',
      '600': '#1e88e5',
      '700': '#1976d2',
      '800': '#1565c0',
      '900': '#0d47a1'
    },
    secondary: {
      '50': '#fce4ec',
      '100': '#f8bbd0',
      '200': '#f48fb1',
      '300': '#f06292',
      '400': '#ec407a',
      '500': '#e91e63',
      '600': '#d81b60',
      '700': '#c2185b',
      '800': '#ad1457',
      '900': '#880e4f'
    },
    neutral: {
      '50': '#fafafa',
      '100': '#f5f5f5',
      '200': '#eeeeee',
      '300': '#e0e0e0',
      '400': '#bdbdbd',
      '500': '#9e9e9e',
      '600': '#757575',
      '700': '#616161',
      '800': '#424242',
      '900': '#212121'
    },
    semantic: {
      error: {
        light: '#ffebee',
        default: '#f44336',
        dark: '#c62828'
      },
      warning: {
        light: '#fff8e1',
        default: '#ffc107',
        dark: '#ff8f00'
      },
      success: {
        light: '#e8f5e9',
        default: '#4caf50',
        dark: '#2e7d32'
      },
      info: {
        light: '#e3f2fd',
        default: '#2196f3',
        dark: '#0d47a1'
      }
    },
    theme: {
      background: {
        primary: '#FFFFFF',
        secondary: '#F9FAFB',
        tertiary: '#E5E7EB'
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
        tertiary: '#9e9e9e'
      },
      border: {
        light: '#e0e0e0',
        medium: '#bdbdbd',
        dark: '#9e9e9e'
      }
    }
  },
  spacing: {
    '0': '0',
    '1': '1px',
    '2': '2px',
    '4': '4px',
    '8': '8px',
    '12': '12px',
    '16': '16px',
    '24': '24px',
    '32': '32px',
    '40': '40px',
    '48': '48px',
    '64': '64px',
    '80': '80px',
    '96': '96px',
    '128': '128px'
  },
  typography: {
    fontFamily: {
      primary: 'Roboto, "Helvetica Neue", Arial, sans-serif',
      secondary: 'Georgia, Times, "Times New Roman", serif'
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem'
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      none: '1',
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
      loose: '2'
    }
  },
  layout: {
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
    }
  },
  inset: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.25rem',
    lg: '0.5rem',
    xl: '1rem',
    full: '9999px',
  },
  breakpoints: {
    xs: '0px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    md: '0 4px 6px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.12), 0 3px 3px rgba(0, 0, 0, 0.08)'
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    medium: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)'
  }
};

/**
 * Checks if a theme is valid by verifying essential properties
 */
const isValidTheme = (theme: any): boolean => {
  if (!theme) return false;
  
  // Check for essential theme properties
  if (!theme.colors || typeof theme.colors !== 'object') return false;
  if (!theme.spacing || typeof theme.spacing !== 'object') return false;
  
  // Check for essential color properties
  if (!theme.colors.primary || !theme.colors.theme) return false;
  if (!theme.colors.theme.text || !theme.colors.theme.background) return false;
  
  return true;
};

/**
 * Merges a potentially incomplete theme with the fallback theme
 */
const createSafeTheme = (theme: Partial<Theme> | undefined): Theme => {
  if (!theme) return fallbackTheme;
  
  // Start with the fallback theme
  const safeTheme = { ...fallbackTheme };
  
  // Recursively merge the provided theme with the fallback
  const deepMerge = (target: any, source: any) => {
    for (const key in source) {
      if (source[key] instanceof Object && key in target) {
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  };
  
  try {
    deepMerge(safeTheme, theme);
  } catch (error) {
    console.warn('Error merging themes:', error);
  }
  
  return safeTheme;
};

// Create a function to apply theme to CSS variables
const applyThemeToCSS = (theme: Theme): void => {
  if (typeof document === 'undefined') return;

  try {
    // Apply theme to CSS variables
    // Primary colors
    Object.entries(theme.colors.primary).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--primary-${key}`, value);
    });
    
    // Secondary colors
    Object.entries(theme.colors.secondary).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--secondary-${key}`, value);
    });
    
    // Neutral colors
    Object.entries(theme.colors.neutral).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--neutral-${key}`, value);
    });
    
    // Background colors
    document.documentElement.style.setProperty('--background-primary', theme.colors.theme.background.primary);
    document.documentElement.style.setProperty('--background-secondary', theme.colors.theme.background.secondary);
    document.documentElement.style.setProperty('--background-tertiary', theme.colors.theme.background.tertiary);
    
    // Text colors
    document.documentElement.style.setProperty('--text-primary', theme.colors.theme.text.primary);
    document.documentElement.style.setProperty('--text-secondary', theme.colors.theme.text.secondary);
    document.documentElement.style.setProperty('--text-tertiary', theme.colors.theme.text.tertiary);
    
    // Border colors
    document.documentElement.style.setProperty('--border-light', theme.colors.theme.border.light);
    document.documentElement.style.setProperty('--border-medium', theme.colors.theme.border.medium);
    document.documentElement.style.setProperty('--border-dark', theme.colors.theme.border.dark);
    
    // Semantic colors
    document.documentElement.style.setProperty('--success-light', theme.colors.semantic.success.light);
    document.documentElement.style.setProperty('--success-default', theme.colors.semantic.success.default);
    document.documentElement.style.setProperty('--success-dark', theme.colors.semantic.success.dark);
    
    document.documentElement.style.setProperty('--error-light', theme.colors.semantic.error.light);
    document.documentElement.style.setProperty('--error-default', theme.colors.semantic.error.default);
    document.documentElement.style.setProperty('--error-dark', theme.colors.semantic.error.dark);
    
    document.documentElement.style.setProperty('--warning-light', theme.colors.semantic.warning.light);
    document.documentElement.style.setProperty('--warning-default', theme.colors.semantic.warning.default);
    document.documentElement.style.setProperty('--warning-dark', theme.colors.semantic.warning.dark);
    
    document.documentElement.style.setProperty('--info-light', theme.colors.semantic.info.light);
    document.documentElement.style.setProperty('--info-default', theme.colors.semantic.info.default);
    document.documentElement.style.setProperty('--info-dark', theme.colors.semantic.info.dark);
    
    // Spacing
    Object.entries(theme.spacing).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--spacing-${key}`, value);
    });
    
    // Typography
    Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--font-size-${key}`, value);
    });
    
    Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--font-weight-${key}`, value.toString());
    });
    
    // Also set global theme object for styled-components
    if (typeof window !== 'undefined') {
      window.__THEME_DATA__ = theme;
    }
  } catch (error) {
    console.error('Error applying theme to CSS variables:', error);
  }
};

interface ThemeProviderSafetyProps {
  theme?: Partial<Theme>;
  children: React.ReactNode;
}

/**
 * A safety wrapper around ThemeProvider that ensures a theme is always available
 * 
 * This component:
 * 1. Validates the provided theme
 * 2. Merges it with fallback values for missing properties
 * 3. Listens for theme safety check events
 * 4. Provides a consistent theme interface to styled-components
 */
const ThemeProviderSafety: React.FC<ThemeProviderSafetyProps> = ({
  theme,
  children
}) => {
  const [safeTheme, setSafeTheme] = useState<Theme>(createSafeTheme(theme));
  const [mode, setMode] = useState<ThemeMode>('light');
  
  // Listen for the custom event from preload.js
  useEffect(() => {
    const handleThemeSafetyCheck = (event: Event) => {
      // If theme is invalid, ensure we're using the safe theme
      if (!isValidTheme(theme)) {
        const newSafeTheme = createSafeTheme(theme);
        setSafeTheme(newSafeTheme);
        applyThemeToCSS(newSafeTheme);
      }
    };
    
    window.addEventListener('connectpath:theme-safety-check', handleThemeSafetyCheck);
    
    // Also check immediately
    if (!isValidTheme(theme)) {
      const newSafeTheme = createSafeTheme(theme);
      setSafeTheme(newSafeTheme);
      applyThemeToCSS(newSafeTheme);
    }
    
    return () => {
      window.removeEventListener('connectpath:theme-safety-check', handleThemeSafetyCheck);
    };
  }, [theme]);
  
  // Update safe theme when the provided theme changes
  useEffect(() => {
    const newSafeTheme = createSafeTheme(theme);
    setSafeTheme(newSafeTheme);
    applyThemeToCSS(newSafeTheme);
  }, [theme]);
  
  // Toggle between light and dark mode
  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };
  
  // Create the context value
  const contextValue = {
    theme: safeTheme,
    mode,
    setMode,
    toggleMode,
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProviderSafety;